"use client";

import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import { useState } from "react";
import {
  Container,
  Content,
  ContentWrapper,
  Title,
  FAQItem,
  Question,
  Answer,
  Category,
} from "./page.styles";

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      category: "Getting Started",
      items: [
        {
          q: "How do I create an account?",
          a: "Click 'Sign Up' on the homepage, enter your information, and verify your email address. You'll be ready to start ordering!",
        },
        {
          q: "Is Drizl available in my area?",
          a: "Check our homepage to see available artisans near you. We're constantly expanding to new areas.",
        },
        {
          q: "Do I need to download an app?",
          a: "No! You can use Drizl directly from your web browser, though we also offer mobile apps for iOS and Android.",
        },
      ],
    },
    {
      category: "Ordering",
      items: [
        {
          q: "How do I place an order?",
          a: "Browse meals, add items to your cart, choose pickup or delivery, and complete your order with secure payment.",
        },
        {
          q: "Can I customize my order?",
          a: "Many artisans offer customization options. Check each meal's details and communicate with the artisan through the app.",
        },
        {
          q: "How far in advance should I order?",
          a: "We recommend ordering at least 24 hours in advance, but some artisans accept same-day orders.",
        },
        {
          q: "Can I cancel or modify my order?",
          a: "Yes, before the artisan begins preparation. Check the order details for the cancellation deadline.",
        },
      ],
    },
    {
      category: "Payment",
      items: [
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards, debit cards, and digital payment methods.",
        },
        {
          q: "When am I charged?",
          a: "You're charged when you place your order. If cancelled before preparation begins, you'll receive a full refund.",
        },
        {
          q: "Are there any fees?",
          a: "There may be delivery fees depending on distance. All fees are clearly shown before you complete your order.",
        },
        {
          q: "How do refunds work?",
          a: "Refunds are processed to your original payment method within 5-7 business days.",
        },
      ],
    },
    {
      category: "Delivery & Pickup",
      items: [
        {
          q: "What are the delivery options?",
          a: "Choose between delivery to your address or pickup from the artisan's location.",
        },
        {
          q: "How much does delivery cost?",
          a: "Delivery fees vary based on distance and are shown before you checkout.",
        },
        {
          q: "Can I track my order?",
          a: "Yes! You'll receive real-time updates on your order status through the app and email.",
        },
        {
          q: "What if I'm not home for delivery?",
          a: "Contact the artisan through the app to arrange an alternative. Some artisans offer contactless delivery.",
        },
      ],
    },
    {
      category: "Safety & Quality",
      items: [
        {
          q: "Are artisans verified?",
          a: "Yes, all artisans go through our verification process and must comply with local food safety regulations.",
        },
        {
          q: "What if I have food allergies?",
          a: "Always check meal descriptions for allergen information and communicate your allergies to the artisan before ordering.",
        },
        {
          q: "What if something is wrong with my order?",
          a: "Contact support immediately at support@drizl.app or use the 'Report Issue' button in your order details.",
        },
      ],
    },
  ];

  return (
    <Container>
      <PublicHeader />
      <Content>
        <ContentWrapper>
          <Title>Frequently Asked Questions</Title>
          {faqs.map((category, catIndex) => (
            <div key={catIndex}>
              <Category>{category.category}</Category>
              {category.items.map((item, itemIndex) => {
                const globalIndex = catIndex * 100 + itemIndex;
                return (
                  <FAQItem key={globalIndex}>
                    <Question
                      onClick={() =>
                        setOpenIndex(
                          openIndex === globalIndex ? null : globalIndex
                        )
                      }
                    >
                      {item.q}
                      <span>{openIndex === globalIndex ? "−" : "+"}</span>
                    </Question>
                    <Answer $isOpen={openIndex === globalIndex}>
                      {item.a}
                    </Answer>
                  </FAQItem>
                );
              })}
            </div>
          ))}
        </ContentWrapper>
      </Content>
      <Footer />
    </Container>
  );
}
